import {
  TenantConfig,
  applyTenantTheme,
  cacheTenantConfig,
  clearCachedTenantConfig,
  readCachedTenantConfig,
} from './tenant-theme';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

/**
 * Descobre de qual cliente e esta area de membros e pinta a marca dele.
 *
 * Roda ANTES do primeiro render de proposito: se o React montasse primeiro, o
 * visitante veria a marca padrao piscar antes da marca do cliente. O cache em
 * localStorage cobre o instante da primeira pintura em visitas seguintes.
 */
/**
 * De qual cliente e esta aba, em desenvolvimento.
 *
 * Em producao quem responde isso e o Host: `escola.membros.x-treed.com` e um
 * cliente, `x-treed.com` e a plataforma — sem estado nenhum. Em dev tudo mora
 * em localhost, entao `?tenant=` faz esse papel e a escolha precisa sobreviver
 * a navegacao da SPA, que perde a query string ao trocar de rota.
 *
 * O detalhe que faltava: guardar a escolha tornava a raiz inalcancavel. Depois
 * de visitar `?tenant=codenauta`, abrir `/login` continuava mostrando Codenauta
 * para sempre, porque "sem parametro" caia na escolha anterior.
 *
 * A regra abaixo separa os dois casos. Carregar uma ROTA DE RAIZ sem parametro
 * e alguem digitando o endereco da plataforma: limpa. Carregar qualquer outra
 * rota sem parametro e um refresh no meio da sessao: mantem.
 */
function resolverClienteEmDev(): string | null {
  const CHAVE = 'xt:dev-tenant';
  const params = new URLSearchParams(window.location.search);

  if (params.has('tenant')) {
    const slug = params.get('tenant');
    if (slug) {
      localStorage.setItem(CHAVE, slug);
      return slug;
    }
    // `?tenant=` vazio: pedido explicito de voltar para a plataforma.
    localStorage.removeItem(CHAVE);
    return null;
  }

  // Rotas que so existem na plataforma: nunca vestem a marca de um cliente.
  const RAIZ = ['/login', '/platform'];
  if (RAIZ.includes(window.location.pathname)) {
    localStorage.removeItem(CHAVE);
    return null;
  }

  return localStorage.getItem(CHAVE);
}

export async function bootstrapTenant(): Promise<TenantConfig | null> {
  // Quem e o cliente vem PRIMEIRO. O tema em cache so pode ser aplicado depois
  // de saber que ainda ha cliente: aplicar antes fazia a raiz herdar a marca da
  // ultima area visitada, e o cache mantinha essa marca viva mesmo depois de o
  // slug ja ter sido esquecido.
  const headers: Record<string, string> = {};
  let semCliente = false;

  // Em dev tudo roda em localhost, entao o Host nao distingue cliente:
  // ?tenant=slug permite abrir dois clientes lado a lado.
  if (import.meta.env.DEV) {
    const active = resolverClienteEmDev();
    if (active) headers['x-tenant-slug'] = active;
    else semCliente = true;
  }

  if (semCliente) {
    clearCachedTenantConfig();
    return null;
  }

  const cached = readCachedTenantConfig();
  if (cached) applyTenantTheme(cached);

  try {
    const response = await fetch(`${API_URL}/tenant/config`, {
      headers,
      cache: 'no-store',
    });
    if (!response.ok) return cached;

    const body = await response.json();
    const config: TenantConfig = body?.data ?? body;
    applyTenantTheme(config);
    cacheTenantConfig(config);
    return config;
  } catch {
    // API fora do ar nao pode impedir o app de montar: sem config o app sobe
    // com a aparencia neutra e as telas tratam o erro normalmente.
    return cached;
  }
}
